import { Image, useImageUrl } from "@car/ui/components/image";
import { Skeleton } from "@car/ui/components/skeleton";
import { ArrowLeft02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "@tanstack/react-router";
import { useRef } from "react";

import {
  bodyLabels,
  fuelLabels,
  platePriceTitle,
  platePriceWords,
  primaryPlateText,
  type SiteVehicle,
} from "@/lib/content";
import { faNumber, faPlainNumber } from "@/lib/format";
import { gsap, useGSAP } from "@/lib/gsap";

/**
 * ویترین: فهرستِ درشت‌نویسِ خودروها. روی دسکتاپ، عکسِ هر ردیف در یک لنز دنبال اشاره‌گر می‌آید
 * (همان لنزِ نورِ هیرو، این بار پرِ عکس). روی لمسی، عکس کوچک کنار هر ردیف است.
 */
export function Lineup({ vehicles, loading }: { vehicles: SiteVehicle[]; loading: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useLineupLens(ref, loading, vehicles.length);
  // the lens swaps its src itself: one optimized size (22rem, sharp on 2x screens) per vehicle
  const imageUrl = useImageUrl();
  const lensImage = (src: string) => imageUrl(src, 704);

  if (loading) {
    return (
      <div className="lineup" aria-busy="true">
        {[0, 1, 2, 3].map((key) => (
          <div key={key} className="lineup-row lineup-row--skeleton">
            <Skeleton className="h-10 w-2/5" />
            <Skeleton className="h-5 w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className="lineup">
      <ol className="lineup-list">
        {vehicles.map((vehicle) => (
          <li key={vehicle.id} className="lineup-row" data-image={lensImage(vehicle.image)}>
            <Link
              to="/vehicles/$vehicleId"
              params={{ vehicleId: vehicle.id }}
              className="lineup-link"
            >
              <Image className="lineup-thumb" src={vehicle.image} alt="" width={104} height={78} />
              <span className="lineup-body">
                <span className="lineup-name">{vehicle.name}</span>
                <span className="lineup-meta">
                  <span>{bodyLabels[vehicle.body]}</span>
                  <span>{fuelLabels[vehicle.fuel]}</span>
                  <span>مدل {faPlainNumber(vehicle.year)}</span>
                </span>
              </span>
              <span className="lineup-price">
                <span className="lineup-amount" title={platePriceTitle(vehicle.price)}>
                  {platePriceWords(vehicle.price)}
                </span>
                {/* سطر جدا (lineup-monthly تمام‌عرض است) تا نام پلاک ستون قیمت را روی موبایل پهن نکند */}
                <span className="lineup-monthly">{primaryPlateText(vehicle)}</span>
                {vehicle.monthlyFrom === null ? null : (
                  <span className="lineup-monthly">
                    اقساط از {faNumber(vehicle.monthlyFrom)} تومان
                  </span>
                )}
              </span>
              <HugeiconsIcon
                icon={ArrowLeft02Icon}
                strokeWidth={1.8}
                className="lineup-arrow size-6"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ol>
      <div className="lineup-lens" aria-hidden="true">
        <img
          src={vehicles[0] ? lensImage(vehicles[0].image) : undefined}
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  );
}

/** بعد از رسیدن داده دوباره بسته می‌شود؛ در حالت اسکلت هنوز فهرستی نیست که لنز به آن بچسبد */
function useLineupLens(
  ref: React.RefObject<HTMLDivElement | null>,
  loading: boolean,
  count: number,
) {
  useGSAP(
    () => {
      const root = ref.current;
      const lens = root?.querySelector<HTMLElement>(".lineup-lens");
      const image = lens?.querySelector("img");
      if (!root || !lens || !image) return;
      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      // عکس‌ها را پیش از اولین hover گرم می‌کنیم تا لنز هیچ‌وقت خالی باز نشود
      // (window.Image: سازنده‌ی DOM، نه کامپوننت Image که این فایل وارد می‌کند)
      root.querySelectorAll<HTMLElement>(".lineup-row[data-image]").forEach((row) => {
        const warm = new window.Image();
        warm.src = row.dataset.image ?? "";
      });

      const toX = gsap.quickTo(lens, "x", { duration: 0.55, ease: "power3" });
      const toY = gsap.quickTo(lens, "y", { duration: 0.55, ease: "power3" });
      const toRotate = gsap.quickTo(lens, "rotation", { duration: 0.8, ease: "power2" });
      let lastX = 0;
      // تا اولین حرکتِ اشاره‌گر جای لنز معلوم نیست؛ قبل از آن نشان داده نمی‌شود
      let placed = false;

      const move = (event: PointerEvent) => {
        const bounds = root.getBoundingClientRect();
        const x = event.clientX - bounds.left;
        const y = event.clientY - bounds.top;
        placed = true;
        toX(x);
        toY(y);
        // چرخشِ ملایم به جهت حرکت دست، مثل کارتی که در هوا می‌چرخد
        toRotate(gsap.utils.clamp(-6, 6, (x - lastX) * 0.25));
        lastX = x;
      };
      const show = (event: Event) => {
        // SAFETY: pointerover targets inside the list are DOM elements
        const row = (event.target as HTMLElement).closest<HTMLElement>(".lineup-row");
        const src = row?.dataset.image;
        if (!src || !placed) return;
        if (image.getAttribute("src") !== src) image.setAttribute("src", src);
        gsap.to(lens, {
          autoAlpha: 1,
          scale: 1,
          duration: 0.45,
          ease: "expo.out",
          overwrite: "auto",
        });
      };
      const hide = () => {
        gsap.to(lens, {
          autoAlpha: 0,
          scale: 0.86,
          duration: 0.35,
          ease: "power2.out",
          overwrite: "auto",
        });
      };

      // لنز سمت چپِ دست می‌نشیند (نه زیرش) تا نامِ ردیف خوانا بماند
      gsap.set(lens, { autoAlpha: 0, scale: 0.86, xPercent: -108, yPercent: -50 });
      root.addEventListener("pointermove", move);
      root.addEventListener("pointerover", show);
      root.addEventListener("pointerleave", hide);
      return () => {
        root.removeEventListener("pointermove", move);
        root.removeEventListener("pointerover", show);
        root.removeEventListener("pointerleave", hide);
      };
    },
    { scope: ref, dependencies: [loading, count], revertOnUpdate: true },
  );
}
